import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { useCartContext } from '../../../Contexts/CartContext';
import { useCartHook } from '../../../Hooks/CartHook';
import { formatCurrency } from '../../../Utils/Formatters';
import { TbAlertCircle, TbCircleCheck } from 'react-icons/tb';
import ButtonPrimary from '../../Form/ButtonPrimary';
import Button from '../../Form/Button';
import { RiCoinsFill } from "react-icons/ri";

interface FidelityModalProps {
    open: boolean;
    onClose: () => void;
    onRefresh?: () => void;
    cart?: any;
}

const FidelityModal = ({ open, onClose, onRefresh, cart }: FidelityModalProps) => {
    const { t } = useTranslation();
    const { company } = useLayoutContext();
    const { user, refreshUser } = useLoginContext();
    const { currentCart } = useCartContext();
    const { applyFidelityDiscount, deleteFidelityDiscount } = useCartHook();

    const [step, setStep] = useState(1);
    const [pointsToUse, setPointsToUse] = useState<number | string>('');
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (open) {
            setStep(currentCart?.value_points ? 2 : 1);
            setPointsToUse('');
            setErrorMsg('');
            refreshUser();
        }
    }, [open, currentCart, refreshUser]);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (open && step === 1) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [open, step]);

    if (!company?.fidelity || !user) return null;

    const { fidelity } = company;
    const currentCartData = cart || currentCart;
    const pointsApplied = Number(currentCartData?.amount_points || currentCartData?.points_used || 0);
    const availablePoints = Number(user.all_points ?? (typeof user.fidelity_points === 'number' ? user.fidelity_points : 0)) - pointsApplied;
    const pointValue = fidelity.point_value;
    const currencyName = fidelity.currency_name;
    const cartTotal = currentCartData?.total || (Number(currentCartData?.total_with_discount || 0) + Number(currentCartData?.extra_tax || 0)) || 0;

    const isHalfFidelityPointsLimit = company?.isHalfFidelityPointsLimit || false;
    const maxPointsAvailable = isHalfFidelityPointsLimit ? Math.floor(availablePoints / 2) : availablePoints;
    const maxPointsForCart = Math.floor(cartTotal / pointValue);
    const maxPointsAllowed = Math.min(maxPointsAvailable, maxPointsForCart);

    const handleApplyPoints = async () => {
        const points = Number(pointsToUse);

        if (isNaN(points) || points <= 0 || !Number.isInteger(points)) {
            setErrorMsg(t('checkout.fidelity.invalid_points'));
            return;
        }

        if (points > availablePoints) {
            setErrorMsg(t('checkout.fidelity.not_enough_points'));
            return;
        }

        if (points > maxPointsAllowed) {
            setErrorMsg(t('checkout.fidelity.exceeds_limit', { max: maxPointsAllowed }));
            return;
        }

        if (points * pointValue > cartTotal) {
            setErrorMsg(t('checkout.fidelity.exceeds_cart_total'));
            return;
        }

        try {
            const response = await applyFidelityDiscount(points);
            if (response?.success) {
                setStep(2);
                if (onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('Error applying fidelity points:', error);
        }
    };

    const handleRemovePoints = async () => {
        try {
            const response = await deleteFidelityDiscount();
            if (response?.success) {
                onClose();
                if (onRefresh) onRefresh();
            }
        } catch (error) {
            console.error('Error removing fidelity points:', error);
        }
    };

    return (
        <Modal open={open} onClose={onClose} className="max-w-md mx-4">
            <div className="p-6">
                <div className="flex flex-col items-center text-center gap-4">

                    <div className='flex flex-col w-full gap-6'>
                        {step === 1 ? (
                            <>
                                <div className="flex flex-col items-center gap-1">
                                    <h2 className="flex items-center justify-center gap-1 text-3xl font-extrabold text-shopfood-title leading-none">
                                        <RiCoinsFill size={35} /> {fidelity.currency_name}
                                    </h2>
                                    <p className="font-medium text-gray-500">
                                        {t('checkout.fidelity.balance_info', {
                                            points: availablePoints,
                                            currency: currencyName,
                                            value: formatCurrency(availablePoints * pointValue)
                                        })}
                                    </p>
                                </div>

                                <div className="w-full bg-gray-50 rounded-xl p-4 ring-1 ring-gray-100">
                                    <h3 className="font-bold text-shopfood-title">
                                        {t('checkout.fidelity.points_to_use')}
                                    </h3>
                                    <div className="relative flex flex-col gap-2">
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            inputMode="numeric"
                                            value={pointsToUse}
                                            onChange={(e) => setPointsToUse(e.target.value)}
                                            placeholder={t('checkout.fidelity.input_placeholder')}
                                            autoFocus
                                            min={0}
                                            className="w-full text-center text-3xl text-shopfood-title font-extrabold placeholder:text-base placeholder:font-normal bg-transparent border-b-2 border-gray-200 focus:border-custom-primary outline-none py-2"
                                        />

                                        <div className="text-sm text-gray-400">
                                            {pointsToUse !== '' && (
                                                <div className="text-sm text-shopfood-title font-bold">
                                                    = {formatCurrency(Number(pointsToUse) * pointValue)} {t('checkout.fidelity.in_discount')}
                                                </div>
                                            )}
                                            <div>
                                                {t('checkout.fidelity.max_allowed', {
                                                    max: maxPointsAllowed,
                                                    currency: currencyName
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {errorMsg && (
                                    <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg w-full">
                                        <TbAlertCircle className="flex-shrink-0" />
                                        <span>{errorMsg}</span>
                                    </div>
                                )}

                                <div className="flex items-center gap-2 w-full mt-0.5">
                                    <ButtonPrimary
                                        disabled={!pointsToUse || Number(pointsToUse) <= 0}
                                        onClick={handleApplyPoints}
                                        className="flex-1 text-nowrap"
                                    >
                                        {t('checkout.fidelity.use_points')}
                                    </ButtonPrimary>
                                    <Button
                                        onClick={onClose}
                                        className="flex-1 text-nowrap"
                                    >
                                        {t('layout.confirm.label_cancel_button')}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="w-full flex flex-col items-center gap-2">
                                    <div className="text-shopfood-success">
                                        <TbCircleCheck size={50} />
                                    </div>
                                    <p className="font-bold text-lg text-shopfood-title leading-none">
                                        {t('checkout.fidelity.discount_applied')}
                                    </p>
                                    <span className="text-4xl font-extrabold text-shopfood-title leading-none">
                                        {formatCurrency(currentCartData?.value_points || 0)}
                                    </span>
                                </div>

                                <div className="w-full text-sm divide-y divide-gray-100">
                                    <div className="flex justify-between py-1 px-4">
                                        <span>{t('checkout.fidelity.points_used')}</span>
                                        <span className="font-extrabold text-shopfood-title">{pointsApplied}</span>
                                    </div>
                                    <div className="flex justify-between py-1 px-4">
                                        <span>{t('checkout.fidelity.remaining_balance')}</span>
                                        <span className="font-extrabold text-shopfood-title">{availablePoints}</span>
                                    </div>
                                </div>

                                <div className="flex gap-2 w-full">
                                    <ButtonPrimary
                                        onClick={onClose}
                                        className="flex-1"
                                    >
                                        {t('layout.confirm.label_close_button')}
                                    </ButtonPrimary>
                                    <Button
                                        onClick={handleRemovePoints}
                                        className="text-red-500 flex-1 text-sm"
                                    >
                                        {t('checkout.fidelity.remove_discount')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FidelityModal;
